import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  RotateCcw,
  PenSquare,
  Check,
  Clock3,
  Send,
  Wand2,
  FileText,
  Eye,
  Edit3,
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import AiRfpService from '../../services/aiRfpService';
import RfpTemplateService from '../../services/rfpTemplateService';
import SourcingRfpService from '../../services/sourcingRFPService';

// ─────────────────────────────────────────────
// Builder helpers (from RfpBuilder.js)
// ─────────────────────────────────────────────
const DEFAULT_EOI_TEMPLATE = `
<h1>NOTICE INVITING EXPRESSION OF INTEREST (EOI)</h1>

<p>{{authority_name}}</p>
<p>{{address}}</p>

<h2>Introduction</h2>
<p>{{introduction}}</p>

<h2>Project Background</h2>
<p>{{project_background}}</p>

<h2>Qualifying Requirements</h2>
<ul>
  {{#each eligibility_points}}
  <li>{{this}}</li>
  {{/each}}
</ul>

<h2>Detailed Term Sheet</h2>
<table>
  <tr><th>Particular</th><th>Details</th></tr>
  {{#each term_sheet}}
  <tr>
    <td>{{label}}</td>
    <td>{{value}}</td>
  </tr>
  {{/each}}
</table>

<h2>Financial Bid</h2>
<p>{{financial_bid_details}}</p>
`.trim();

const REQUIRED_TEMPLATE_MARKERS = [
  /EXPRESSION OF INTEREST/i,
  /Introduction/i,
  /Project Background/i,
  /Requirements/i,
  /Term Sheet/i,
  /Financial Bid/i,
];

const htmlToPlainText = (html = '') =>
  String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const htmlToBulletList = (html = '') => {
  const liMatches = String(html).match(/<li>(.*?)<\/li>/gi) || [];
  const bullets = liMatches.map((li) => htmlToPlainText(li)).filter(Boolean);
  return bullets.length ? bullets : [htmlToPlainText(html)].filter(Boolean);
};

const initialBuilderState = {
  overview: {
    title: '',
    authorityName: '',
    address: '',
    projectType: '',
    department: '',
    location: '',
    budget: '',
    timeline: '',
  },
  introduction: '<p>Provide introduction of the authority and EOI invitation summary.</p>',
  projectBackground: '<p>Provide project background and objective details.</p>',
  eligibility: '<ul><li>Define bidder eligibility and qualification criteria.</li></ul>',
  termSheetRows: [
    { label: 'EMD', value: '' },
    { label: 'Tender Fee', value: '' },
    { label: 'Completion Timeline', value: '' },
  ],
  financialBid: '<p>Define financial bid submission details and pricing instructions.</p>',
  htmlContent: DEFAULT_EOI_TEMPLATE,
  attachments: [],
};

const buildStateFromNavigation = (state) => {
  const generated = state?.generatedContent || {};
  const payload = state?.inputPayload || {};
  const templateVariableMap = state?.templateVariableMap || {};
  const selectedTemplateContent = state?.selectedTemplateContent || '';

  const fromGenerated = (sectionKey) => {
    const points = generated?.[sectionKey]?.points || [];
    if (!Array.isArray(points) || points.length === 0) return '';
    return `<ul>${points.map((p) => `<li>${p}</li>`).join('')}</ul>`;
  };

  const termSheetRowsFromState =
    templateVariableMap.term_sheet ||
    generated?.termSheet?.rows ||
    initialBuilderState.termSheetRows;

  return {
    ...initialBuilderState,
    overview: {
      ...initialBuilderState.overview,
      title: payload.projectName || '',
      authorityName: payload.department || '',
      address: payload.location || '',
      projectType: payload.projectType || '',
      department: payload.department || '',
      location: payload.location || '',
      budget: payload.budget || '',
      timeline: payload.timeline || '',
    },
    introduction: (generated?.introduction?.points || []).length
      ? `<p>${generated.introduction.points.join(' ')}</p>`
      : fromGenerated('scope') ||
      (templateVariableMap.introduction
        ? `<p>${templateVariableMap.introduction}</p>`
        : initialBuilderState.introduction),
    projectBackground: (generated?.projectBackground?.points || []).length
      ? `<p>${generated.projectBackground.points.join(' ')}</p>`
      : fromGenerated('technical') ||
      (templateVariableMap.project_background
        ? `<p>${templateVariableMap.project_background}</p>`
        : initialBuilderState.projectBackground),
    eligibility: (generated?.qualifyingRequirements?.points || []).length
      ? `<ul>${generated.qualifyingRequirements.points.map((p) => `<li>${p}</li>`).join('')}</ul>`
      : fromGenerated('eligibility') ||
      (Array.isArray(templateVariableMap.eligibility_points)
        ? `<ul>${templateVariableMap.eligibility_points.map((p) => `<li>${p}</li>`).join('')}</ul>`
        : initialBuilderState.eligibility),
    termSheetRows: Array.isArray(termSheetRowsFromState)
      ? termSheetRowsFromState
      : initialBuilderState.termSheetRows,
    financialBid: (generated?.financialBid?.points || []).length
      ? `<p>${generated.financialBid.points.join(' ')}</p>`
      : fromGenerated('commercial') ||
      (templateVariableMap.financial_bid_details
        ? `<p>${templateVariableMap.financial_bid_details}</p>`
        : initialBuilderState.financialBid),
    htmlContent: selectedTemplateContent || DEFAULT_EOI_TEMPLATE,
  };
};

// ─────────────────────────────────────────────
// AI Preview helpers (from AiRfpOutputValidation.js)
// ─────────────────────────────────────────────
const buildFallbackContent = () => ({
  scope: {
    title: 'Scope',
    points: [
      'Define project objectives and deliverables.',
      'Specify milestones and acceptance criteria.',
      'Outline expected service levels and quality benchmarks.',
    ],
  },
  technical: {
    title: 'Technical',
    points: [
      'List technical requirements and architecture constraints.',
      'Define integration touchpoints and security controls.',
      'Specify performance and compliance expectations.',
    ],
  },
  eligibility: {
    title: 'Eligibility',
    points: [
      'Vendor qualification and mandatory certifications.',
      'Relevant domain experience and references.',
      'Financial and legal eligibility checks.',
    ],
  },
  commercial: {
    title: 'Commercial',
    points: [
      'Commercial submission format and pricing model.',
      'Payment milestones and contractual assumptions.',
      'Commercial evaluation and award conditions.',
    ],
  },
});

const sectionOrder = ['scope', 'technical', 'eligibility', 'commercial'];

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const RfpWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { success, error: showError, toasts, removeToast } = useToast();

  // Determine initial tab — if we have generated AI content, open preview first
  const hasGeneratedContent = !!location.state?.generatedContent;
  const [activeTab, setActiveTab] = useState(hasGeneratedContent ? 'preview' : 'builder');

  // ── AI Preview State ──────────────────────────────────────────────
  const incomingGenerated = location.state?.generatedContent;
  const inputPayload = location.state?.inputPayload || null;
  const flowSource = location.state?.source || 'workspace';
  const isManualTemplateFlow = flowSource === 'template-manual-generate';
  const promptConfigInjection = location.state?.promptConfigInjection || null;
  const draftId = location.state?.draftId || null;
  const [generatedContent] = useState(incomingGenerated || buildFallbackContent());
  const [isRegenerating, setIsRegenerating] = useState(false);

  const sections = useMemo(
    () => sectionOrder.map((key) => generatedContent?.[key]).filter(Boolean),
    [generatedContent],
  );

  const templateVariableMap = useMemo(() => {
    const introPoints = generatedContent?.scope?.points || [];
    const bgPoints = generatedContent?.technical?.points || [];
    const eligibility = generatedContent?.eligibility?.points || [];
    const commercialPoints = generatedContent?.commercial?.points || [];
    return {
      introduction: htmlToPlainText(introPoints.join(' ')),
      project_background: htmlToPlainText(bgPoints.join(' ')),
      eligibility_points: eligibility,
      financial_bid_details: htmlToPlainText(commercialPoints.join(' ')),
      term_sheet: [
        { label: 'Bid Type', value: 'EOI' },
        { label: 'Evaluation Mode', value: 'Two Stage' },
      ],
    };
  }, [generatedContent]);

  // ── Builder State ─────────────────────────────────────────────────
  const [version] = useState(1);
  const [saving, setSaving] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateInputValues, setTemplateInputValues] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    location.state?.selectedTemplateId || '',
  );
  const splitPaneRef = useRef(null);
  const [builderData, setBuilderData] = useState(
    buildStateFromNavigation({
      ...location.state,
      templateVariableMap,
    }),
  );

  // ── Effects ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isResizing) return undefined;
    const onMouseMove = (event) => {
      if (!splitPaneRef.current) return;
      const rect = splitPaneRef.current.getBoundingClientRect();
      const raw = ((event.clientX - rect.left) / rect.width) * 100;
      setSplitRatio(Math.min(70, Math.max(30, raw)));
    };
    const onMouseUp = () => setIsResizing(false);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await RfpTemplateService.getTemplates();
        const list = Array.isArray(res.data) ? res.data : [];
        setTemplates(list);
        if (!selectedTemplateId && list.length > 0) {
          const first = list[0];
          setSelectedTemplateId(first._id);
          if (!location.state?.selectedTemplateContent && first.content) {
            setBuilderData((prev) => ({ ...prev, htmlContent: first.content }));
          }
        }
      } catch {
        setTemplates([]);
      }
    };
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTemplateInputValues((prev) => {
      const defaults = {
        authority_name: builderData.overview.authorityName || builderData.overview.department,
        address: builderData.overview.address || builderData.overview.location,
        introduction: htmlToPlainText(builderData.introduction),
        project_background: htmlToPlainText(builderData.projectBackground),
        financial_bid_details: htmlToPlainText(builderData.financialBid),
      };
      return { ...defaults, ...prev };
    });
  }, [
    builderData.financialBid,
    builderData.introduction,
    builderData.overview.address,
    builderData.overview.authorityName,
    builderData.overview.department,
    builderData.overview.location,
    builderData.projectBackground,
  ]);

  // ── AI Preview Actions ────────────────────────────────────────────
  const saveStructuredDraft = () => {
    try {
      localStorage.setItem(
        'rfp_ai_generated_draft',
        JSON.stringify({ savedAt: new Date().toISOString(), source: 'workspace', generatedContent, inputPayload }),
      );
    } catch (err) {
      console.warn('Unable to save local AI draft snapshot', err);
    }
  };

  const handleRegenerate = async () => {
    if (isManualTemplateFlow) {
      saveStructuredDraft();
      navigate('/sourcing/rfps/create');
      return;
    }
    try {
      setIsRegenerating(true);
      const response = await AiRfpService.generateDraft({
        draftId,
        inputPayload,
        promptConfig: promptConfigInjection,
      });
      const regeneratedContent = response?.data?.generatedContent;
      const nextDraftId = response?.data?.draftId || draftId;
      if (!regeneratedContent) throw new Error('No regenerated AI content returned.');
      success('AI RFP regenerated successfully.');
      navigate('/sourcing/rfps/create/workspace', {
        replace: true,
        state: { generatedContent: regeneratedContent, inputPayload, promptConfigInjection, draftId: nextDraftId },
      });
    } catch (err) {
      saveStructuredDraft();
      showError(err?.message || err?.response?.data?.message || 'Failed to regenerate AI RFP. Local snapshot preserved.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // ── Builder Helpers ───────────────────────────────────────────────
  const rfpId = location.state?.rfpId || location.state?.sourceRfpId || null;

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl._id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  const templateValidationErrors = useMemo(() => {
    const htmlContent = builderData.htmlContent || '';
    return REQUIRED_TEMPLATE_MARKERS.map((pattern, idx) => ({
      ok: pattern.test(htmlContent),
      label: `Rule ${idx + 1}`,
    })).filter((rule) => !rule.ok);
  }, [builderData.htmlContent]);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const found = templates.find((tpl) => tpl._id === templateId);
    if (!found) return;
    setBuilderData((prev) => ({
      ...prev,
      htmlContent: found.content || prev.htmlContent,
      overview: { ...prev.overview, projectType: found.projectType || prev.overview.projectType },
    }));
  };

  const setOverviewField = (field, value) =>
    setBuilderData((prev) => ({ ...prev, overview: { ...prev.overview, [field]: value } }));

  const setSectionHtml = (sectionKey, html) =>
    setBuilderData((prev) => ({ ...prev, [sectionKey]: html }));

  const buildTemplateVariableMapFromBuilder = () => ({
    authority_name: builderData.overview.authorityName || builderData.overview.department,
    address: builderData.overview.address || builderData.overview.location,
    introduction: htmlToPlainText(builderData.introduction),
    project_background: htmlToPlainText(builderData.projectBackground),
    eligibility_points: htmlToBulletList(builderData.eligibility),
    term_sheet: builderData.termSheetRows,
    financial_bid_details: htmlToPlainText(builderData.financialBid),
    ...location.state?.templateVariableMap,
    ...templateInputValues,
  });

  const handleTemplateTokenChange = (token, value) => {
    setTemplateInputValues((prev) => ({ ...prev, [token]: value }));
    if (token === 'authority_name') setOverviewField('authorityName', value);
    if (token === 'address') setOverviewField('address', value);
    if (token === 'introduction') setSectionHtml('introduction', `<p>${value}</p>`);
    if (token === 'project_background') setSectionHtml('projectBackground', `<p>${value}</p>`);
    if (token === 'financial_bid_details') setSectionHtml('financialBid', `<p>${value}</p>`);
  };

  const buildTemplateEditorHtml = () => {
    const variableMap = buildTemplateVariableMapFromBuilder();
    let html = String(builderData.htmlContent || '');
    html = html.replace(
      /\{\{#each\s+([^}]+)\}\}([\s\S]*?)\{\{\/each\}\}/g,
      (_match, arrayKey, inner) => {
        const rows = variableMap[String(arrayKey).trim()];
        if (!Array.isArray(rows) || rows.length === 0) return '';
        return rows.map((row) => {
          if (typeof row === 'string') return String(inner).replace(/\{\{\s*this\s*\}\}/g, row);
          return String(inner)
            .replace(/\{\{\s*this\s*\}\}/g, '')
            .replace(/\{\{\s*label\s*\}\}/g, String(row?.label || ''))
            .replace(/\{\{\s*value\s*\}\}/g, String(row?.value || ''));
        }).join('');
      },
    );
    return html;
  };

  const renderTemplatePreview = () => {
    const variableMap = buildTemplateVariableMapFromBuilder();
    let html = String(builderData.htmlContent || '');
    html = html.replace(
      /\{\{#each\s+([^}]+)\}\}([\s\S]*?)\{\{\/each\}\}/g,
      (_match, arrayKey, inner) => {
        const rows = variableMap[String(arrayKey).trim()];
        if (!Array.isArray(rows) || rows.length === 0) return '';
        return rows.map((row) => {
          if (typeof row === 'string') return String(inner).replace(/\{\{\s*this\s*\}\}/g, row);
          return String(inner)
            .replace(/\{\{\s*this\s*\}\}/g, '')
            .replace(/\{\{\s*label\s*\}\}/g, String(row?.label || ''))
            .replace(/\{\{\s*value\s*\}\}/g, String(row?.value || ''))
            .replace(/\{\{\s*([^#/][^}]*)\s*\}\}/g, (_t, token) => String(row?.[String(token).trim()] || ''));
        }).join('');
      },
    );
    html = html.replace(/\{\{\s*([^#/][^}]*)\s*\}\}/g, (_m, token) => {
      const value = variableMap[String(token).trim()];
      if (value === null || value === undefined) return '';
      if (Array.isArray(value)) return value.join(', ');
      return String(value);
    });
    return html;
  };

  const renderTemplateEditorFillBlanks = () => {
    const sourceHtml = buildTemplateEditorHtml();
    const renderTextWithInputs = (text, keyPrefix) => {
      const parts = [];
      const regex = /\{\{\s*([^#/][^}]*)\s*\}\}/g;
      let lastIndex = 0;
      let match;
      let tokenIndex = 0;
      while ((match = regex.exec(text)) !== null) {
        const tokenName = String(match[1] || '').trim();
        const start = match.index;
        if (start > lastIndex) parts.push(text.slice(lastIndex, start));
        parts.push(
          <span key={`${keyPrefix}-input-${tokenIndex}`} style={{ display: 'inline-flex', alignItems: 'baseline', margin: '0 4px', fontFamily: 'Consolas, "Courier New", monospace', color: '#475569' }}>
            <span>{'{' + '{'}</span>
            <input
              value={templateInputValues[tokenName] || ''}
              onChange={(e) => handleTemplateTokenChange(tokenName, e.target.value)}
              placeholder={tokenName}
              style={{ border: 'none', background: 'transparent', outline: 'none', margin: '0 2px', padding: 0, fontFamily: 'inherit', color: 'inherit', minWidth: '120px', width: `${Math.max(10, String(templateInputValues[tokenName] || tokenName).length)}ch` }}
            />
            <span>{'}' + '}'}</span>
          </span>,
        );
        tokenIndex += 1;
        lastIndex = regex.lastIndex;
      }
      if (lastIndex < text.length) parts.push(text.slice(lastIndex));
      return parts;
    };

    const renderDomNode = (node, keyPrefix) => {
      if (node.nodeType === 3) return renderTextWithInputs(node.textContent || '', keyPrefix);
      if (node.nodeType !== 1) return null;
      const tagName = node.tagName.toLowerCase();
      const props = { key: keyPrefix };
      Array.from(node.attributes || []).forEach((attr) => {
        if (attr.name === 'class') props.className = attr.value;
        else props[attr.name] = attr.value;
      });
      const children = Array.from(node.childNodes || []).flatMap((child, idx) =>
        renderDomNode(child, `${keyPrefix}-${idx}`),
      );
      return React.createElement(tagName, props, children.length > 0 ? children : null);
    };

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${sourceHtml}</div>`, 'text/html');
      const root = doc.body.firstChild;
      if (!root) return null;
      return (
        <div style={{ border: '1px solid var(--border-color)', background: '#fff', minHeight: 'calc(72vh - 48px)', maxHeight: 'calc(72vh - 48px)', overflow: 'auto', padding: '12px', lineHeight: 1.5 }}>
          {Array.from(root.childNodes).flatMap((child, idx) => renderDomNode(child, `fill-${idx}`))}
        </div>
      );
    } catch {
      return (
        <div style={{ border: '1px solid var(--border-color)', background: '#fff', minHeight: 'calc(72vh - 48px)', maxHeight: 'calc(72vh - 48px)', overflow: 'auto', padding: '12px' }}>
          <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{sourceHtml}</pre>
        </div>
      );
    }
  };

  const handleCreateRfp = async () => {
    if (templateValidationErrors.length > 0) {
      showError('Template structure is invalid. Resolve missing required sections first.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        projectName: builderData.overview.title || inputPayload?.projectName || 'Untitled RFP',
        projectType: builderData.overview.projectType || inputPayload?.projectType || '',
        department: builderData.overview.department || builderData.overview.authorityName || inputPayload?.department || '',
        location: builderData.overview.location || builderData.overview.address || inputPayload?.location || '',
        scope: htmlToPlainText(renderTemplatePreview()),
        budget: builderData.overview.budget || inputPayload?.budget || '',
        timeline: builderData.overview.timeline || inputPayload?.timeline || '',
        templateSelection: selectedTemplate?.templateName || location.state?.selectedTemplateName || '',
      };
      const draftResponse = await AiRfpService.createDraft(payload, null);
      const createdDraftId = draftResponse?.data?._id;
      const generateResponse = await AiRfpService.generateDraft({ draftId: createdDraftId || rfpId || null, inputPayload: payload, promptConfig: null });
      const generatedContentData = generateResponse?.data?.generatedContent || {};
      const generatedScope = [
        ...(generatedContentData?.scope?.points || []),
        ...(generatedContentData?.technical?.points || []),
        ...(generatedContentData?.eligibility?.points || []),
        ...(generatedContentData?.commercial?.points || []),
      ].map((point) => String(point || '').trim()).filter(Boolean).join(' ');
      const lineItems = (builderData.termSheetRows || [])
        .map((row, idx) => ({ description: String(row?.label || '').trim() || `Line Item ${idx + 1}`, requirement: String(row?.value || '').trim() }))
        .filter((row) => row.description);
      const createdRfpResponse = await SourcingRfpService.createRfp({ title: payload.projectName, scope: generatedScope || payload.scope, lineItems, status: 'DRAFT' });
      const createdRfpId = (createdRfpResponse?.data || createdRfpResponse)?._id;
      success('RFP created successfully.');
      if (createdRfpId) navigate(`/sourcing/rfps/processing/${createdRfpId}`);
      else navigate('/sourcing/rfps');
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || 'Failed to create RFP.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  const tabs = [
    { id: 'preview', label: 'AI Generated Preview', icon: <Eye size={16} /> },
    { id: 'builder', label: 'Document Builder', icon: <Edit3 size={16} /> },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="RFP Workspace"
        description="View AI-generated content and edit your RFP in a unified workspace."
      />

      <div className="page-container">
        <div className="d-flex flex-wrap align-items-center justify-content-between border-b border-gray-200 mb-6">
          <div className="d-flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 transition-all font-medium ${activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="d-flex align-items-center gap-3 pe-3 pb-2 pb-md-0">
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 text-muted small fw-medium" style={{ whiteSpace: 'nowrap' }}>Active Template:</label>
              <select
                className="form-select form-select-sm"
                value={selectedTemplateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                style={{ borderRadius: '6px', minWidth: '240px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}
              >
                <option value="">Select template...</option>
                {templates.map((tpl) => (
                  <option key={tpl._id} value={tpl._id}>{tpl.templateName}</option>
                ))}
              </select>
            </div>
            {selectedTemplate?.projectType && (
              <span className="badge bg-blue-50 text-blue-600 border border-blue-100 px-2 py-1" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                {selectedTemplate.projectType}
              </span>
            )}
          </div>
        </div>

        {/* ── AI Generated Preview Tab ── */}
        {activeTab === 'preview' && (
          <div>

            <div className="row g-3">
              {sections.map((section, sectionIndex) => (
                <div className="col-lg-6" key={`${section.title || 'section'}-${sectionIndex}`}>
                  <div className="card h-100" style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}>
                    <div className="card-body" style={{ padding: '18px' }}>
                      <h6 className="mb-2">{section.title}</h6>
                      <ul className="mb-0" style={{ paddingLeft: '18px' }}>
                        {(section.points || []).map((point, pointIndex) => (
                          <li key={`${section.title}-${pointIndex}-${point}`} style={{ fontSize: '13px', marginBottom: '6px' }}>
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card mt-3" style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}>
              <div className="card-body" style={{ padding: '18px' }}>
                <h6 className="mb-2">AI Output → Template Variable Mapping</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr><th>AI Output</th><th>Template Field</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Introduction</td><td>{'{{introduction}}'}</td></tr>
                      <tr><td>Project Background</td><td>{'{{project_background}}'}</td></tr>
                      <tr><td>Qualifying Requirements</td><td>{'{{eligibility_points}}'}</td></tr>
                      <tr><td>Financial Bid</td><td>{'{{financial_bid_details}}'}</td></tr>
                      <tr><td>Term Sheet (Rows)</td><td>{'{{term_sheet}}'}</td></tr>
                    </tbody>
                  </table>
                </div>

                <div className="d-flex gap-2 justify-content-end mt-4 pt-4 border-top" style={{ flexWrap: 'wrap' }}>
                  <button className="btn btn-outline-secondary" onClick={handleRegenerate} disabled={isRegenerating}>
                    <RotateCcw size={16} className="me-1" />
                    {isManualTemplateFlow ? 'Back to Inputs' : isRegenerating ? 'Regenerating...' : 'Regenerate'}
                  </button>
                  <button className="btn btn-outline-primary" onClick={() => setActiveTab('builder')}>
                    <PenSquare size={16} className="me-1" />
                    Edit in Builder
                  </button>
                  <button className="btn btn-primary" onClick={() => setActiveTab('builder')}>
                    <Check size={16} className="me-1" />
                    Accept & Build
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'builder' && (
          <div>
            <div className="card" style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}>
              <div className="card-body" style={{ padding: '20px' }}>
                <div ref={splitPaneRef} style={{ display: 'flex', alignItems: 'stretch', minHeight: '72vh', maxHeight: '72vh', userSelect: isResizing ? 'none' : 'auto' }}>
                  {/* Editor Pane */}
                  <div style={{ width: `${splitRatio}%`, minWidth: '320px', paddingRight: '10px', overflow: 'auto' }}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <FileText size={16} />
                      <h6 className="mb-0">Editor</h6>
                    </div>
                    <div>
                      {renderTemplateEditorFillBlanks()}
                      {templateValidationErrors.length > 0 && (
                        <p className="mt-2 mb-0" style={{ color: '#9a3412', fontSize: '12px' }}>
                          Missing required template markers. Keep headings, term-sheet table, and list sections.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Divider */}
                  <div
                    role="separator"
                    aria-orientation="vertical"
                    onMouseDown={() => setIsResizing(true)}
                    style={{ width: '10px', cursor: 'col-resize', background: isResizing ? '#d1d5db' : '#e5e7eb', borderRadius: '4px', alignSelf: 'stretch', margin: '0 6px' }}
                  />

                  {/* Preview Pane */}
                  <div style={{ width: `${100 - splitRatio}%`, minWidth: '320px', paddingLeft: '10px', overflow: 'auto' }}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <FileText size={16} />
                      <h6 className="mb-0">Preview</h6>
                    </div>
                    <div
                      style={{ border: '1px solid var(--border-color)', background: '#fff', minHeight: 'calc(72vh - 48px)', maxHeight: 'calc(72vh - 48px)', overflow: 'auto', padding: '12px' }}
                      dangerouslySetInnerHTML={{ __html: renderTemplatePreview() }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '20px 0 0', gap: '12px', flexWrap: 'wrap' }}>
                  {hasGeneratedContent && (
                    <button className="btn btn-outline-secondary" onClick={() => setActiveTab('preview')}>
                      <Eye size={16} className="me-1" />
                      View AI Preview
                    </button>
                  )}
                  <button className="btn btn-outline-secondary" onClick={() => navigate('/sourcing/rfps')}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateRfp}
                    disabled={saving}
                  >
                    <Send size={16} className="me-1" />
                    {saving ? 'Creating...' : 'Create RFP'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default RfpWorkspace;
